import type { SubscriptionDetails } from "schemas/generated/billing";

import React from "react";

import Button from "@replo/design-system/components/button/Button";
import { Menu } from "@replo/design-system/components/menu/Menu";
import { CircleEllipsis, CircleX } from "lucide-react";

import { getStripePortalUrl } from "./sharedBillingUtils";

const ManagePlanMenu = ({
  workspaceId,
  isWorkspaceOwner,
  activeSubscription,
  handleCancelPlan,
}: {
  workspaceId: string;
  isWorkspaceOwner: boolean;
  activeSubscription: SubscriptionDetails;
  handleCancelPlan: () => void;
}) => {
  const isPlanCustom =
    activeSubscription.isManagedByReplo ||
    activeSubscription.v1BillingTier === "custom";

  let cancelPlanTooltipText: string | undefined;
  if (!isWorkspaceOwner) {
    cancelPlanTooltipText =
      "Only Workspace Owners have the ability to cancel plans";
  } else if (isPlanCustom) {
    cancelPlanTooltipText =
      "To cancel a custom plan, please contact Replo support at support@replo.app";
  }

  const isCancelPlanEnabled = isWorkspaceOwner && !isPlanCustom;

  const isSubscriptionAlreadyCanceled =
    activeSubscription.endedAt && activeSubscription.endedAt > new Date();

  const isStripeSubscription = activeSubscription.paymentProcessor === "stripe";

  const menuItems = React.useMemo(() => {
    const items = [];

    if (isStripeSubscription) {
      items.push({
        type: "leaf" as const,
        id: "edit-payment",
        title: "Edit Payment Details",
        variant: "default" as const,
        startEnhancer: <CircleEllipsis size={12} />,
        onSelect: () => {
          window.open(getStripePortalUrl(workspaceId), "_blank");
        },
      });
    }

    if (!isSubscriptionAlreadyCanceled) {
      items.push({
        type: "leaf" as const,
        id: "cancel-plan",
        title: "Cancel Plan",
        variant: "danger" as const,
        disabled: !isCancelPlanEnabled,
        toolTipText: cancelPlanTooltipText,
        startEnhancer: <CircleX size={12} />,
        onSelect: () => {
          handleCancelPlan();
        },
      });
    }

    return items;
  }, [
    workspaceId,
    isSubscriptionAlreadyCanceled,
    isCancelPlanEnabled,
    cancelPlanTooltipText,
    isStripeSubscription,
    handleCancelPlan,
  ]);

  if (menuItems.length === 0) {
    return null;
  }

  return (
    <Menu
      items={menuItems}
      align="start"
      trigger={
        <Button variant="secondary" size="sm">
          Edit
        </Button>
      }
    />
  );
};

export default ManagePlanMenu;
